// @flow
import { useState } from 'react';

// The file types the backend's document upload accepts (pdf, jpeg, jpg, png, docx, xlsx, xml).
export const CORRECTED_LEASE_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/xml',
  'text/xml',
];

export const CORRECTED_LEASE_MAX_SIZE_MB = 100;
// The save route's payload limit (100 * 1000 * 1000 bytes).
const MAX_SIZE_BYTES = CORRECTED_LEASE_MAX_SIZE_MB * 1000 * 1000;

type Selection = {
  /** The file the dialog would save: the held one until changed, null once removed. */
  file: ?File,
  /** An oversized file just refused, named in the error. */
  tooLargeFileName: ?string,
  /** Remounts the uploader, back to `file`, after refusing an oversized one. */
  uploaderKey: number,
};

const fromHeldFile = (heldFile: ?File): Selection => ({
  file: heldFile ?? null,
  tooLargeFileName: null,
  uploaderKey: 0,
});

/**
 * The Upload Corrected Lease dialog's selection. Each opening starts from the held file.
 *
 * The size limit is checked here, not through FileUploader's `maxFileSizeInMB`: the library
 * (7.10.0) compares the size in KB against that value, so any file over 100 KB would be refused.
 * An oversized file is refused with its own message, and the uploader is reset to the selection it
 * had.
 */
export const useCorrectedLeaseUpload = ({
  open,
  heldFile,
  onSave,
}: {
  open: boolean,
  heldFile: ?File,
  onSave: (file: ?File) => void,
}): ({
  selection: ?File,
  tooLargeFileName: ?string,
  uploaderKey: number,
  canSave: boolean,
  handleChange: (value: File | Array<File> | void) => void,
  handleSave: () => void,
}) => {
  const [selection, setSelection] = useState<Selection>(() =>
    fromHeldFile(heldFile),
  );
  // Reset while rendering the opening, not in an effect: the uploader mounts with the opening and
  // must get the held file as its initial file.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelection(fromHeldFile(heldFile));
    }
  }

  // FileUploader is uncontrolled: it reports its file after mount and on every add or remove
  // (undefined once removed).
  const handleChange = (value: File | Array<File> | void) => {
    const file = (Array.isArray(value) ? value[0] : value) ?? null;
    setSelection((current) => {
      if (file === current.file) {
        return current;
      }
      if (file && file.size > MAX_SIZE_BYTES) {
        return {
          ...current,
          tooLargeFileName: file.name,
          uploaderKey: current.uploaderKey + 1,
        };
      }
      return { ...current, file, tooLargeFileName: null };
    });
  };

  return {
    selection: selection.file,
    tooLargeFileName: selection.tooLargeFileName,
    uploaderKey: selection.uploaderKey,
    // A new file, or the held file removed: either is a change to save.
    canSave: selection.file !== (heldFile ?? null),
    handleChange,
    handleSave: () => onSave(selection.file),
  };
};
